/**
 * ReportBlockMenu — reusable WEB UGC moderation control (parity with the
 * mobile ReportBlockSheet).
 *
 * Apple App Store Guideline 1.2 + Google Play UGC policy require an in-app way
 * to (a) report objectionable content and (b) block abusive users on EVERY
 * user-generated-content surface. This "⋯" menu is shared by the web
 * EntityComments and CommunityChat surfaces so they match the mobile app.
 *
 * Props:
 *   targetUserId   — author of the content (used as the block target + testid)
 *   targetUsername — display name on the Block row
 *   contentLabel   — 'comment' | 'message'
 *   onReport(reason) — async; report the content with the chosen reason
 *   onBlock()        — async; block the author
 */
import { useState } from 'react';
import { MoreVertical, Flag, Ban } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';

const REASONS = [
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'hate', label: 'Hate speech' },
  { value: 'violence', label: 'Violence or threats' },
  { value: 'sexual', label: 'Sexual or explicit content' },
  { value: 'defamation', label: 'False / defamatory claim' },
  { value: 'doxxing', label: 'Sharing private information' },
  { value: 'spam', label: 'Spam or scam' },
  { value: 'other', label: 'Something else' },
];

export default function ReportBlockMenu({
  targetUserId,
  targetUsername = 'this user',
  contentLabel = 'content',
  onReport,
  onBlock,
}) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleReason = async (reason) => {
    setBusy(true);
    try { await onReport(reason); } finally { setBusy(false); setReasonOpen(false); }
  };
  const handleBlock = async () => {
    setBusy(true);
    try { await onBlock(); } finally { setBusy(false); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="opacity-50 hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-secondary/60"
            aria-label={`Report or block ${targetUsername}`}
            data-testid={`report-block-menu-${targetUserId}`}
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); setReasonOpen(true); }}
            data-testid="report-content-option"
          >
            <Flag className="h-3.5 w-3.5 mr-2" /> Report {contentLabel}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); handleBlock(); }}
            className="text-destructive focus:text-destructive"
            data-testid="block-user-option"
          >
            <Ban className="h-3.5 w-3.5 mr-2" /> Block {targetUsername}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={reasonOpen} onOpenChange={setReasonOpen}>
        <DialogContent className="max-w-sm" data-testid="report-reason-dialog">
          <DialogHeader>
            <DialogTitle>Why are you reporting this?</DialogTitle>
            <DialogDescription>
              Your report is sent to our moderation team for review. Abusive users can be blocked separately.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 mt-2">
            {REASONS.map((r) => (
              <Button
                key={r.value}
                variant="outline"
                className="justify-start text-sm"
                onClick={() => handleReason(r.value)}
                disabled={busy}
                data-testid={`report-reason-${r.value}`}
              >
                {r.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
